import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PlatformAuthContext = createContext(null);

export const usePlatformAuth = () => {
  const context = useContext(PlatformAuthContext);
  if (!context) {
    throw new Error('usePlatformAuth must be used within PlatformAuthProvider');
  }
  return context;
};

export const PlatformAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const logout = useCallback(() => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_user');
    setUser(null);
    setToken(null);
  }, []);

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/platform/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    
    localStorage.setItem('platform_token', data.access_token);
    localStorage.setItem('platform_user', JSON.stringify(data.user));
    
    setToken(data.access_token);
    setUser(data.user);
    
    return data;
  };

  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const currentToken = token || localStorage.getItem('platform_token');
    
    if (!currentToken) {
      throw new Error('No authentication token');
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${currentToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 401) {
      logout();
      throw new Error('Session expired');
    }

    return response;
  }, [API_URL, token, logout]);

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('platform_token');
    const storedUser = localStorage.getItem('platform_user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch {
        logout();
      }
    }
    
    setLoading(false);
  }, [logout]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    fetchWithAuth,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    isFinanceManager: user?.role === 'FINANCE_MANAGER' || user?.role === 'SUPER_ADMIN',
    isSalesManager: user?.role === 'SALES_MANAGER' || user?.role === 'SUPER_ADMIN',
    isSalesRep: user?.role === 'SALES_REP'
  };

  return (
    <PlatformAuthContext.Provider value={value}>
      {children}
    </PlatformAuthContext.Provider>
  );
};

export default PlatformAuthContext;
